// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina
/* eslint-disable react-refresh/only-export-components */

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file MessagesContext.jsx
 * @description Store central de mensajes. WS → addMessage (solo realtime); REST/merge vía mergeConversation.
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  reconcileMessages,
  sortMessagesChronological,
  getMessageSortTimestamp,
} from '../utils/reconcileMessages';
import { toConversationId } from '../utils/toConversationId';
import { normalizeMessage } from '../utils/normalizeMessage';
import { canMerge, canSet } from '../utils/storeGuards';
import socketService, { resolveConversationId, normalizeIncomingMessage } from '../services/socketService';
import {
  mergePendingSnapshotOnlyFromPayload,
  clearPendingIncomingForConversation,
} from '../utils/pendingIncomingConversationHighlights';
import { devLog, isArchatDebugSubjectMessages, isArchatDebugMessageOrder } from '../utils/devConsoleLog';
import { isArchatI18nV1BlobString } from '../utils/archatNoticePayload';
import { normalizeLang } from '../i18n/langNormalize.js';
import { STORAGE_KEYS } from '../utils/constants.js';
import { logEvent } from '../utils/runtimeLogger.js';
import { debugLog, debugError } from '../utils/debugLogger.js';
import { FEATURES } from '../config/featureFlags';
import { buildMessageScopeStoreKey } from '../utils/messageScopeStoreKey';

/**
 * Orden estable al mutar buckets por WS (append/replace); logs solo con ARCHAT_DEBUG_MESSAGE_ORDER.
 * @param {object[]} arr
 * @param {string} tag
 * @param {string} storeKey
 * @param {string} cidDisplay
 * @returns {object[]}
 */
function finalizeBucketOrder(arr, tag, storeKey, cidDisplay) {
  const input = Array.isArray(arr) ? arr : [];
  const sorted = sortMessagesChronological(input);
  if (isArchatDebugMessageOrder()) {
    try {
      // eslint-disable-next-line no-console
      console.info('[ARCHAT_DEBUG_MESSAGE_ORDER][store.bucket]', {
        tag,
        storeKey,
        cid: cidDisplay,
        idsBefore: input.map((m) => m?.id ?? m?.mensaje_id ?? m?.__id),
        idsAfter: sorted.map((m) => m?.id ?? m?.mensaje_id ?? m?.__id),
        fechasAfter: sorted.map((m) => ({
          id: m?.id ?? m?.mensaje_id,
          fecha_envio: m?.fecha_envio ?? '',
          sortTsMs: getMessageSortTimestamp(m),
        })),
      });
    } catch (_) {
      /* noop */
    }
  }
  return sorted;
}

/** @typedef {Record<string, object[]>} MessagesByConversation — claves string: "42" o "42:subject:9" (Asuntos V1) */

/** @typedef {Record<number, { name: string, ts: number }>} TypingUsersByConversation - conversation_id -> { name, ts } para auto-limpieza */
const MessagesContext = createContext({
  /** @type {MessagesByConversation} */
  messagesByConversation: {},
  /** @type {TypingUsersByConversation} */
  typingUsers: {},
  /** @type {string} */
  activeConversationId: '',
  /** @type {string} */
  activeConversationIdNormalized: '',
  /** @param {string|number|null|undefined} id */
  setActiveConversationId: () => {},
  /** @type {Set<string>} */
  favoriteIds: new Set(),
  /** @type {React.Dispatch<React.SetStateAction<Set<string>>>} */
  setFavoriteIds: () => {},
  addMessage: /** @param {number} cid @param {object} msg */ () => {},
  updateMessage: /** @param {number} cid @param {string|number} messageId @param {object} patch */ () => {},
  setConversationMessages: /** @param {number} cid @param {object[]} list @param {'fetch'|'socket'|'init'} source */ () => {},
  mergeConversation: /** @param {number} cid @param {object[]} apiMessages @param {boolean} apiSuccess @param {{ preserveRealtime?: boolean }} [mergeOptions] */ () => {},
  getMessages: /** @param {number} cid @returns {object[]} */ () => [],
  /** Optimista: mensaje propio antes del ACK/WS (solo UI). */
  appendOptimisticSelfMessage: /** @param {string|number} cid @param {{ text: string, senderId: number }} p @returns {string|null} temp key */ () => null,
});

function isDeletedMessage(msg) {
  const eliminado = msg?.eliminado;
  const deletedAt = msg?.deleted_at ?? msg?.deletedAt;
  const deletedAtStr = deletedAt != null ? String(deletedAt).trim() : '';
  const deletedAtMeaningful =
    deletedAtStr !== '' &&
    deletedAtStr !== '0000-00-00 00:00:00' &&
    deletedAtStr !== '0000-00-00';
  return (
    eliminado === 1 ||
    eliminado === true ||
    eliminado === '1' ||
    deletedAtMeaningful
  );
}

/**
 * Clave en `messagesByConversation`.
 * @param {unknown} conversationId
 * @param {{ subjectsFeatureEnabled?: boolean, subjectRowId?: number|null, mergeLegacyPlainBucket?: boolean }|null} [subjectsScope]
 * @returns {string|null}
 */
function conversationStoreKey(conversationId, subjectsScope = null) {
  return buildMessageScopeStoreKey(conversationId, subjectsScope);
}

/** Mensaje entrante/WS: con `subject_id` → bucket scoped; si no, bucket plano (legacy / sin columna). */
function resolveIncomingMessageStoreKey(conversationId, msg) {
  const numericCid = toConversationId(conversationId);
  if (numericCid == null) return null;
  const sid = Number(msg?.subject_id ?? msg?.raw?.subject_id);
  if (Number.isFinite(sid) && sid > 0) {
    return buildMessageScopeStoreKey(numericCid, { subjectsFeatureEnabled: true, subjectRowId: sid });
  }
  return buildMessageScopeStoreKey(numericCid, null);
}

/** Todas las claves de store para una conversación (plano + scopes). */
function conversationBucketKeysForCid(prev, numericCid) {
  const base = String(numericCid);
  return Object.keys(prev).filter((k) => k === base || k.startsWith(`${base}:subject:`));
}

/**
 * ID canónico de mensaje para store/render/dedupe.
 * @param {object|null|undefined} msg
 * @returns {string}
 */
function getMessageId(msg) {
  return String(
    msg?.id ??
      msg?.message_id ??
      msg?.mensaje_id ??
      msg?.temp_id ??
      ''
  );
}

/**
 * temp_id canónico para reconciliar optimista -> confirmado.
 * @param {object|null|undefined} msg
 * @returns {string|null}
 */
function getTempId(msg) {
  return msg?.temp_id ? String(msg.temp_id) : null;
}

function hasAttachmentPayload(msg) {
  if (!msg || typeof msg !== 'object') return false;
  const size = Number(msg?.archivo_tamano ?? msg?.raw?.archivo_tamano ?? 0);
  if (Number.isFinite(size) && size > 0) return true;
  return Boolean(
    msg?.archivo_data ??
      msg?.archivo_url ??
      msg?.archivo_nombre_original ??
      msg?.archivo_mime_type ??
      msg?.raw?.archivo_data ??
      msg?.raw?.archivo_url ??
      msg?.raw?.archivo_nombre_original ??
      msg?.raw?.archivo_mime_type
  );
}

/**
 * Valida inmutabilidad al actualizar el store (raíz y bucket nuevos).
 * @param {Record<string, object[]>} prev
 * @param {Record<string, object[]>} next
 * @param {string} cidKey
 * @param {object[]|undefined} newBucket - array que se asigna a next[cidKey]
 */
function logStateImmutabilityCheck(prev, next, cidKey, newBucket) {
  if (typeof console === 'undefined' || next === prev || newBucket == null) return;
  try {
    console.log('[STATE_REFERENCE_CHECK]', {
      sameRoot: prev === next,
      sameArray: prev[cidKey] === newBucket,
    });
  } catch {
    // noop
  }
}

export const MessagesProvider = ({ children }) => {
  /** @type {[MessagesByConversation, React.Dispatch<React.SetStateAction<MessagesByConversation>>]} */
  const [messagesByConversation, setMessagesByConversation] = useState({});
  /** @type {[TypingUsersByConversation, React.Dispatch<React.SetStateAction<TypingUsersByConversation>>]} */
  const [typingUsers, setTypingUsers] = useState({});
  const [activeConversationId, setActiveConversationIdState] = useState('');
  const activeConversationIdRef = useRef('');
  /** @type {[Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>]} */
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  const setActiveConversationId = useCallback((id) => {
    const val = String(id ?? '').trim();
    setActiveConversationIdState(val);
    if (typeof window !== 'undefined') {
      window.__ACTIVE_CHAT_ID__ = val !== '' ? val : null;
    }
  }, []);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const nextActive = String(activeConversationId ?? '').trim();
    window.__ACTIVE_CHAT_ID__ = nextActive !== '' ? nextActive : null;
    return () => {
      window.__ACTIVE_CHAT_ID__ = null;
    };
  }, [activeConversationId]);

  useEffect(() => {
    socketService.setActiveConversationIdGetter(() => activeConversationIdRef.current);
    return () => socketService.setActiveConversationIdGetter(null);
  }, []);

  const addMessageRef = useRef(null);

  const addMessage = useCallback((conversationId, msg) => {
    /** Solo entrada por WebSocket (processIncomingWsDetail / fallback interno). */
    const numericCid = toConversationId(conversationId);
    const storeKey = resolveIncomingMessageStoreKey(conversationId, msg);
    if (storeKey == null || numericCid == null) {
      if (typeof console !== 'undefined') {
        console.error('[CONV_ID_MISSING_CRITICAL]', msg);
      }
      return;
    }
    if (msg == null || typeof msg !== 'object') return;

    const cidDisplay = String(numericCid);
    const normalizedMessage = {
      ...msg,
      conversation_id: cidDisplay,
      conversacion_id: cidDisplay,
      __id: getMessageId(msg),
    };
    debugLog('ADD_MESSAGE_START', {
      incoming: normalizedMessage,
      conversation_id: cidDisplay,
      storeKey,
    });
    try {
      if (
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('ARCHAT_DEBUG_LANGUAGE_RUNTIME') === '1'
      ) {
        console.info('[LANG_ADD_MESSAGE_CHOICE]', {
          message_id: normalizedMessage?.id ?? normalizedMessage?.mensaje_id ?? null,
          conversation_id: cidDisplay,
          final_text: String(normalizedMessage?.text ?? normalizedMessage?.mensaje ?? '').slice(0, 120),
          contenido_original: String(normalizedMessage?.contenido_original ?? '').slice(0, 80),
          display_text: String(normalizedMessage?.display_text ?? '').slice(0, 80),
          source_language: normalizedMessage?.source_language ?? normalizedMessage?.idioma_original ?? null,
          target_language: normalizedMessage?.target_language ?? null,
          translation_failed: normalizedMessage?.translation_failed ?? null,
          ts: Date.now(),
        });
      }
    } catch (_) { /* noop */ }
    if (isArchatDebugMessageOrder()) {
      try {
        // eslint-disable-next-line no-console
        console.info('[ARCHAT_DEBUG_MESSAGE_ORDER][MessagesContext.addMessage]', {
          storeKey,
          incomingId: normalizedMessage?.id ?? normalizedMessage?.mensaje_id ?? normalizedMessage?.__id,
          fecha_envio: normalizedMessage?.fecha_envio ?? normalizedMessage?.timestamp ?? '',
          subject_id: normalizedMessage?.subject_id ?? normalizedMessage?.raw?.subject_id ?? null,
        });
      } catch (_) {
        /* noop */
      }
    }
    if (isDeletedMessage(normalizedMessage)) return;

    console.log('[DEBUG_FLOW] ADD_MESSAGE_EXEC', {
      conversationId,
      messageId: msg?.id,
      storeKey,
    });

    // Sonido de mensaje entrante: dispara socketService en `chat:message` (no aquí).

    setMessagesByConversation((prev) => {
      let working = { ...prev };
      let list = [...(working[storeKey] || [])];

      const id = normalizedMessage.__id;
      if (!id) {
        debugError('MESSAGE_ID_MISSING', normalizedMessage);
        console.error('[MESSAGE_ID_MISSING]', normalizedMessage);
        return prev;
      }

      debugLog('ADD_MESSAGE_STATE_BEFORE', {
        count: list.length,
        conversation_id: cidDisplay,
        storeKey,
      });

      console.log('[DEBUG_FLOW] STATE_BEFORE_UPDATE', {
        length: list.length,
      });

      const selfId =
        socketService.userId != null ? Number(socketService.userId) : null;
      const incomingSender = Number(
        normalizedMessage.remitente_id ?? normalizedMessage.sender_id ?? -1
      );
      const realIdStr = String(
        normalizedMessage.id ?? normalizedMessage.mensaje_id ?? ''
      );
      const isRealId =
        realIdStr !== '' &&
        !realIdStr.startsWith('temp_') &&
        !Number.isNaN(Number(realIdStr));

      /**
       * Chat nuevo: optimista en bucket de contacto; WS trae `conversation_id` distinto.
       * Migra filas al bucket real y luego reemplaza `is_temp`.
       */
      if (selfId != null && incomingSender === selfId && isRealId) {
        const hasTempInTarget = list.some(
          (m) =>
            m?.is_temp === true &&
            Number(m.remitente_id ?? m.sender_id ?? -1) === selfId
        );
        if (!hasTempInTarget) {
          const oldKey = Object.keys(working).find((k) => {
            if (String(k) === String(storeKey)) return false;
            const b = working[k] || [];
            return b.some(
              (m) =>
                m?.is_temp === true &&
                Number(m.remitente_id ?? m.sender_id ?? -1) === selfId
            );
          });
          if (oldKey != null) {
            const oldList = [...(working[oldKey] || [])];
            const shouldMove = (m) =>
              String(m.conversation_id) === String(oldKey) ||
              (m?.is_temp === true &&
                Number(m.remitente_id ?? m.sender_id ?? -1) === selfId);
            const toMove = oldList.filter(shouldMove);
            const kept = oldList.filter((m) => !shouldMove(m));
            if (toMove.length > 0) {
              if (typeof console !== 'undefined') {
                console.error('[CONV_REMAP]', { from: oldKey, to: storeKey });
              }
              const remapped = toMove.map((m) => ({
                ...m,
                conversation_id: cidDisplay,
                conversacion_id: cidDisplay,
              }));
              working = { ...working };
              if (kept.length === 0) {
                delete working[oldKey];
              } else {
                working[oldKey] = kept;
              }
              const existingInTarget = [...(working[storeKey] || [])];
              working[storeKey] = finalizeBucketOrder(
                [...remapped, ...existingInTarget],
                'remap',
                storeKey,
                cidDisplay
              );
              list = [...(working[storeKey] || [])];
            }
          }
        }

        const tempIdx = list.findIndex(
          (m) =>
            m?.is_temp === true &&
            Number(m.remitente_id ?? m.sender_id ?? -1) === selfId
        );
        if (tempIdx !== -1) {
          const updated = [...list];
          const realId = normalizedMessage.id ?? normalizedMessage.mensaje_id;
          const merged = {
            ...list[tempIdx],
            ...normalizedMessage,
            id: realId,
            mensaje_id: realId,
            __id: String(realId ?? id),
            is_temp: false,
          };
          updated[tempIdx] = merged;
          const ordered = finalizeBucketOrder(updated, 'replace_temp', storeKey, cidDisplay);
          const next = { ...working, [storeKey]: ordered };
          logStateImmutabilityCheck(prev, next, storeKey, next[storeKey]);
          debugLog('ADD_MESSAGE_REPLACE_TEMP_SELF', {
            id: realId,
            conversation_id: cidDisplay,
            storeKey,
          });
          return next;
        }
      }

      const tempId = getTempId(normalizedMessage);
      const existingIndex = list.findIndex((m) => {
        const sameId = m?.__id === id;
        const sameTemp =
          tempId &&
          m?.temp_id &&
          String(m.temp_id) === tempId;
        return sameId || sameTemp;
      });
      if (import.meta.env.DEV) {
        console.debug('[MESSAGE_FLOW]', {
          id: normalizedMessage.__id,
          temp_id: normalizedMessage.temp_id ?? null,
          hasExisting: existingIndex !== -1,
        });
      }
      if (existingIndex !== -1) {
        debugLog('ADD_MESSAGE_DUPLICATE', {
          id: normalizedMessage.__id,
          temp_id: normalizedMessage.temp_id ?? null,
          conversation_id: cidDisplay,
          storeKey,
        });
        const updated = [...list];
        const prevRow = list[existingIndex];
        const isIncomingWsDup =
          selfId != null &&
          Number.isFinite(incomingSender) &&
          incomingSender > 0 &&
          incomingSender !== selfId;
        const prevFe = prevRow?.fecha_envio ?? '';
        const incomingFe = normalizedMessage?.fecha_envio ?? '';
        const prevIsMysqlNaive = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(prevFe);
        const incomingIsIso = /T/.test(incomingFe);
        const mergedDup = {
          ...prevRow,
          ...normalizedMessage,
          __id: id,
          ...(prevIsMysqlNaive && incomingIsIso
            ? { fecha_envio: prevFe, createdAt: prevFe, created_at: prevFe }
            : {}),
        };
        if (prevRow.__entradaAnim === true || isIncomingWsDup) {
          mergedDup.__entradaAnim = true;
        }
        updated[existingIndex] = mergedDup;
        debugLog('ADD_MESSAGE_REPLACE', {
          id: normalizedMessage.__id,
          index: existingIndex,
          conversation_id: cidDisplay,
          storeKey,
        });
        const next = {
          ...working,
          [storeKey]: finalizeBucketOrder(updated, 'dup_replace', storeKey, cidDisplay),
        };
        logStateImmutabilityCheck(prev, next, storeKey, next[storeKey]);
        debugLog('ADD_MESSAGE_STATE_AFTER', {
          count: updated.length,
          conversation_id: cidDisplay,
          storeKey,
        });
        return next;
      }

      logEvent('MESSAGE_APPEND', 'MessagesContext.addMessage', {
        messageId: normalizedMessage.id ?? normalizedMessage.mensaje_id,
        id: normalizedMessage.id ?? normalizedMessage.mensaje_id,
        cid: cidDisplay,
        storeKey,
      });
      if (typeof console !== 'undefined') {
        console.log('[MESSAGE_APPEND_OK]', { id, cid: cidDisplay, storeKey });
      }
      const isIncomingWs =
        selfId != null &&
        Number.isFinite(incomingSender) &&
        incomingSender > 0 &&
        incomingSender !== selfId;
      const toAppend =
        isIncomingWs ? { ...normalizedMessage, __entradaAnim: true } : normalizedMessage;
      const nextList = [...list, toAppend];
      debugLog('ADD_MESSAGE_INSERT', {
        id: normalizedMessage.__id,
        conversation_id: cidDisplay,
        storeKey,
      });
      const next = {
        ...working,
        [storeKey]: finalizeBucketOrder(nextList, 'append', storeKey, cidDisplay),
      };
      logStateImmutabilityCheck(prev, next, storeKey, next[storeKey]);
      console.log('[DEBUG_FLOW] STATE_AFTER_UPDATE');
      debugLog('ADD_MESSAGE_STATE_AFTER', {
        count: next[storeKey]?.length ?? 0,
        conversation_id: cidDisplay,
        storeKey,
      });
      return next;
    });
  }, []);
  useEffect(() => {
    addMessageRef.current = addMessage;
  }, [addMessage]);

  const updateMessageRef = useRef(null);

  const updateMessage = useCallback((conversationId, messageId, patch, subjectsScope) => {
    const numericCid = toConversationId(conversationId);
    if (numericCid == null || messageId == null || typeof patch !== 'object' || patch == null) return;

    const patchList = (list) => {
      let changed = false;
      const mapped = list.map((m) => {
        const mid = m?.id ?? m?.mensaje_id;
        const matches = mid === messageId || String(mid) === String(messageId);
        if (!matches) return m;
        changed = true;
        const merged = { ...m, ...patch };
        if (m.__entradaAnim === true) {
          merged.__entradaAnim = true;
        }
        if (m.raw != null && typeof m.raw === 'object') {
          merged.raw = { ...m.raw, ...patch };
        }
        if (m.__entradaAnim === true && merged.raw && typeof merged.raw === 'object') {
          merged.raw = { ...merged.raw, __entradaAnim: true };
        }
        return merged;
      });
      return { mapped, changed };
    };

    setMessagesByConversation((prev) => {
      if (subjectsScope !== undefined) {
        const key = conversationStoreKey(conversationId, subjectsScope ?? null);
        if (key == null) return prev;
        const list = [...(prev[key] ?? [])];
        const { mapped, changed } = patchList(list);
        if (!changed) return prev;
        const bucket = sortMessagesChronological([...mapped]);
        const next = { ...prev, [key]: bucket };
        logStateImmutabilityCheck(prev, next, key, bucket);
        return next;
      }

      let next = { ...prev };
      let any = false;
      for (const key of conversationBucketKeysForCid(prev, numericCid)) {
        const list = [...(prev[key] ?? [])];
        const { mapped, changed } = patchList(list);
        if (!changed) continue;
        next[key] = sortMessagesChronological([...mapped]);
        any = true;
        logStateImmutabilityCheck(prev, next, key, next[key]);
      }
      return any ? next : prev;
    });
  }, []);

  useEffect(() => {
    updateMessageRef.current = updateMessage;
  }, [updateMessage]);

  const setConversationMessages = useCallback((conversationId, messages, source = 'init', subjectsScope = null) => {
    const key = conversationStoreKey(conversationId, subjectsScope);
    if (key == null) return;
    const cid = toConversationId(conversationId);
    const list = Array.isArray(messages)
      ? [...messages]
        .filter((m) => !isDeletedMessage(m))
        .map(normalizeMessage)
        .map((m) => (m ? { ...m, __id: getMessageId(m) } : m))
        .filter(Boolean)
      : [];
    const orderedList = sortMessagesChronological(list);
    if (typeof console !== 'undefined') {
      devLog('[STATE_SOURCE_UPDATE]', { source, messages_count: orderedList.length });
    }
    devLog('[STORE] setConversationMessages', { cid: key, length: orderedList.length });
    setMessagesByConversation((prev) => {
      const oldList = prev[key] || [];
      const currentLength = oldList.length;
      const guard = canSet(messages, cid, currentLength);
      if (!guard.allowed) return prev;
      const bucket = [...orderedList];
      const next = { ...prev, [key]: bucket };
      logStateImmutabilityCheck(prev, next, key, bucket);
      return next;
    });
  }, []);

  const mergeConversation = useCallback((conversationId, apiMessages, apiSuccess = true, mergeOptions = null) => {
    const subjectsScope = mergeOptions?.subjectsScope ?? null;
    const key = conversationStoreKey(conversationId, subjectsScope);
    if (key == null) return;
    const cid = toConversationId(conversationId);
    const preserveRealtime = mergeOptions?.preserveRealtime !== false;
    setMessagesByConversation((prev) => {
      const realtime = preserveRealtime ? [...(prev[key] ?? [])] : [];
      const currentLength = realtime.length;
      const guard = canMerge(apiSuccess, apiMessages, cid, currentLength);
      devLog('[MSG] STORE BEFORE', { cid: key, stateKeys: Object.keys(prev), realtimeLength: realtime.length });
      if (!guard.allowed) {
        devLog('[STORE] mergeConversation skipped by guard', { cid: key, reason: guard.reason });
        return prev;
      }
      const historyRaw = apiMessages != null && Array.isArray(apiMessages) ? apiMessages : [];
      const history = historyRaw
        .map((m) => normalizeMessage(m))
        .map((m) => (m ? { ...m, __id: getMessageId(m) } : m))
        .filter(Boolean);
      devLog('[STORE][BEFORE]', { cid: key, realtimeLength: realtime.length, historyLength: history.length });
      let merged;
      if (history.length === 0 && realtime.length > 0) {
        merged = sortMessagesChronological([...realtime]);
        devLog('[STORE][MERGE]', { cid: key, skip: 'api_vacio_con_realtime', merged: merged.length });
      } else {
        merged = reconcileMessages([...history], [...realtime]);
        merged = merged.map((m) => (m ? { ...m, __id: getMessageId(m) } : m));
        merged = merged.filter((m) => {
          const id = m?.id ?? m?.mensaje_id;
          if (typeof id !== 'string' || !/^temp[_-]/i.test(String(id))) return true;
          const text = String(m?.text ?? m?.mensaje ?? '');
          const sender = m?.remitente_id ?? m?.userId;
          const ts = getMessageSortTimestamp(m);
          const hasReal = merged.some((o) => {
            const oid = o?.id ?? o?.mensaje_id;
            if (typeof oid === 'string' && /^temp[_-]/i.test(String(oid))) return false;
            return (
              (o?.remitente_id ?? o?.userId) === sender &&
              String(o?.text ?? o?.mensaje ?? '') === text &&
              Math.abs(getMessageSortTimestamp(o) - ts) < 60000
            );
          });
          return !hasReal;
        });
        devLog('[STORE][MERGE]', { cid: key, history: history.length, realtime: realtime.length, merged: merged.length });
      }
      let filteredMerged = merged.filter((m) => !isDeletedMessage(m));
      filteredMerged = sortMessagesChronological(filteredMerged);
      if (isArchatDebugSubjectMessages()) {
        const prevArr = prev[key] ?? [];
        // eslint-disable-next-line no-console
        console.info('[ARCHAT_DEBUG_SUBJECT_MESSAGES][mergeConversation]', {
          scopeKey: key,
          prevCount: prevArr.length,
          newMergedCount: filteredMerged.length,
          mergedIds: filteredMerged.map((m) => m?.id ?? m?.mensaje_id),
        });
      }
      if (isArchatDebugMessageOrder()) {
        const prevArr = prev[key] ?? [];
        try {
          // eslint-disable-next-line no-console
          console.info('[ARCHAT_DEBUG_MESSAGE_ORDER][mergeConversation]', {
            scopeKey: key,
            activeConversationId: activeConversationIdRef.current,
            storeIdsBefore: prevArr.map((m) => m?.id ?? m?.mensaje_id),
            storeIdsAfter: filteredMerged.map((m) => m?.id ?? m?.mensaje_id),
            fechasAfter: filteredMerged.map((m) => ({
              id: m?.id ?? m?.mensaje_id,
              fecha_envio: m?.fecha_envio ?? '',
              sortTsMs: getMessageSortTimestamp(m),
              subject_id: m?.subject_id ?? m?.raw?.subject_id ?? null,
            })),
          });
        } catch (_) {
          /* noop */
        }
      }
      if (typeof console !== 'undefined') {
        devLog('[STATE_SOURCE_UPDATE]', { source: 'fetch', messages_count: filteredMerged.length });
      }
      devLog('[MSG] STORE AFTER', { cid: key, mergedLength: filteredMerged.length });
      const bucket = [...filteredMerged];
      const next = { ...prev, [key]: bucket };
      logStateImmutabilityCheck(prev, next, key, bucket);
      return next;
    });
  }, []);

  const getMessages = useCallback((conversationId, subjectsScope = null) => {
    const key = conversationStoreKey(conversationId, subjectsScope);
    if (key == null) return [];
    let primary = messagesByConversation[key] || [];
    if (
      subjectsScope &&
      subjectsScope.subjectsFeatureEnabled === true &&
      subjectsScope.mergeLegacyPlainBucket === true &&
      subjectsScope.subjectRowId != null
    ) {
      const plainKey = conversationStoreKey(conversationId, null);
      if (plainKey != null && plainKey !== key) {
        const legacy = messagesByConversation[plainKey] || [];
        if (legacy.length === 0) return primary;
        const merged = reconcileMessages([...primary], [...legacy]);
        const withIds = merged
          .map((m) => (m ? { ...m, __id: getMessageId(m) } : m))
          .filter(Boolean);
        return finalizeBucketOrder(withIds, 'getMessages_legacy_merge', key, String(conversationId ?? ''));
      }
    }
    return primary;
  }, [messagesByConversation]);

  /**
   * FASE 1 — Append inmediato del mensaje propio antes de enviar al backend/WS.
   * @param {string|number} conversationId
   * @param {{ text: string, senderId: number }} param1
   * @returns {string|null} id temporal (`temp_${timestamp}`)
   */
  const appendOptimisticSelfMessage = useCallback((conversationId, { text, senderId }, subjectsScope = null) => {
    const numericCid = toConversationId(conversationId);
    const storeKey = conversationStoreKey(conversationId, subjectsScope);
    if (storeKey == null || numericCid == null) return null;
    const trimmed = String(text ?? '').trim();
    const uid = Number(senderId);
    if (!trimmed || !Number.isFinite(uid) || uid <= 0) return null;
    const cidDisplay = String(numericCid);
    const tempKey = `temp_${Date.now()}`;
    const scopeSid =
      subjectsScope?.subjectsFeatureEnabled === true && subjectsScope?.subjectRowId != null
        ? Number(subjectsScope.subjectRowId)
        : NaN;
    const tempMessage = {
      id: tempKey,
      temp_id: tempKey,
      conversation_id: cidDisplay,
      conversacion_id: cidDisplay,
      text: trimmed,
      mensaje: trimmed,
      display_text: trimmed,
      remitente_id: uid,
      sender_id: uid,
      is_temp: true,
      __id: tempKey,
      fecha_envio: new Date().toISOString(),
      ...(Number.isFinite(scopeSid) && scopeSid > 0 ? { subject_id: scopeSid } : {}),
      ...(FEATURES.FEATURE_MESSAGE_STATUS_V1
        ? {
            delivery_status: 'sending',
            status: 'sending',
          }
        : {}),
    };
    if (typeof console !== 'undefined') {
      console.log('[MESSAGE_SELF_APPEND]', tempMessage);
    }
    setMessagesByConversation((prev) => {
      const list = sortMessagesChronological([...(prev[storeKey] || []), tempMessage]);
      const next = { ...prev, [storeKey]: list };
      logStateImmutabilityCheck(prev, next, storeKey, next[storeKey]);
      return next;
    });
    return tempKey;
  }, []);

  const processIncomingWsDetail = useCallback((data, options = {}) => {
    if (!data || typeof data !== 'object') return;
    const rawMsgCheck = data?.mensaje ?? data?.text ?? data?.display_text ?? '';
    const isTraceMsg = typeof rawMsgCheck === 'string' && (rawMsgCheck.includes('PRO_V2_REALTIME_ROOTCAUSE_001') || rawMsgCheck.includes('REVERSE_EN_TO_ES') || rawMsgCheck.includes('FORWARD_ES_TO_EN'));
    if (isTraceMsg) {
      const currentUserId = typeof socketService?.userId !== 'undefined' ? Number(socketService.userId) : null;
      const remitenteId = Number(data?.remitente_id ?? data?.emisor_id ?? data?.emisor ?? 0);
      console.log('[AUTH_DEBUG][MessagesContext_processIncomingWsDetail]', JSON.stringify({
        step: 'ENTRY',
        message_id: data?.id ?? data?.mensaje_id,
        currentUserId,
        remitente_id: remitenteId,
        isOwn: currentUserId != null && remitenteId === currentUserId,
        data_mensaje: String(data?.mensaje ?? '').slice(0, 200),
        data_text: String(data?.text ?? '').slice(0, 200),
        data_display_text: String(data?.display_text ?? '').slice(0, 200),
        data_delivery_view: data?.delivery_view,
        data_delivered_display_text: data?.delivered_display_text?.slice(0, 200),
        data_traducciones_keys: data?.traducciones ? Object.keys(data.traducciones) : null,
        data_translations_map_keys: data?.translations_map ? Object.keys(data.translations_map) : null,
        hasDeliveryView: !!(data?.delivery_view && typeof data.delivery_view === 'object'),
      }));
    }
    if (isTraceMsg) {
      console.log('[DEBUG_TRACE][FE_PROCESS_INCOMING]', JSON.stringify({
        skipAddMessage: options.skipAddMessage,
        data_mensaje: String(data?.mensaje ?? '').slice(0, 200),
        data_display_text: String(data?.display_text ?? '').slice(0, 200),
        data_traducciones: data?.traducciones,
        data_translations_map: data?.translations_map,
      }));
    }
    const skipAddMessage = options.skipAddMessage === true;
    const cid = toConversationId(
      resolveConversationId(data) ?? data.conversacion_id ?? data.conversation_id
    );
    if (cid == null) return;
    const conversationId = String(
      data.conversation_id ?? data.conversacion_id ?? data.conversationId ?? data.cid ?? cid
    );
    const msgWithUnreadFlag = {
      ...data,
      conversation_id: conversationId,
      conversacion_id: conversationId,
      conversationId,
    };

    let payloadForStore = msgWithUnreadFlag;
    try {
      const preserveArchatNoticeBlob =
        payloadForStore &&
        typeof payloadForStore === 'object' &&
        (isArchatI18nV1BlobString(payloadForStore.contenido_original) ||
          isArchatI18nV1BlobString(payloadForStore.mensaje) ||
          isArchatI18nV1BlobString(payloadForStore.text));
      const rawLang =
        typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.LANGUAGE) : null;
      const uiLang = rawLang ? normalizeLang(String(rawLang).trim()) : null;
      // FROZEN: if PHP already delivered a per-user snapshot, NEVER recalculate
      const hasDeliveryView =
        (payloadForStore.delivery_view && typeof payloadForStore.delivery_view === 'object') ||
        (payloadForStore.deliveryView && typeof payloadForStore.deliveryView === 'object');
      if (hasDeliveryView) {
        if (isTraceMsg) {
          console.log('[DEBUG_TRACE][FE_SKIP_TRANSLATION_GUARD] delivery_view present, skipping recalc');
        }
      } else if (!preserveArchatNoticeBlob && uiLang && payloadForStore && typeof payloadForStore === 'object') {
        const map =
          payloadForStore.traducciones ??
          payloadForStore.translations_map ??
          payloadForStore.translations ??
          null;
        if (isTraceMsg) {
          console.log('[DEBUG_TRACE][FE_TRANSLATION_LOOKUP]', JSON.stringify({
            uiLang, hasMap: !!map, mapKeys: map ? Object.keys(map) : null,
            msgId: payloadForStore?.id ?? payloadForStore?.mensaje_id,
          }));
        }
        if (map && typeof map === 'object') {
          const picked = map[uiLang] ?? map[String(uiLang).split(/[-_]/)[0]];
          if (isTraceMsg) {
            console.log('[DEBUG_TRACE][FE_TRANSLATION_PICKED]', JSON.stringify({
              uiLang, picked: picked != null ? String(picked).slice(0, 200) : null,
              isEmpty: picked == null || String(picked).trim() === '',
              originalText: String(payloadForStore?.text ?? payloadForStore?.mensaje ?? '').slice(0, 200),
            }));
          }
          if (picked != null && String(picked).trim() !== '') {
            const t = String(picked).trim();
            logEvent('TRANSLATION_APPLIED', 'MessagesContext.processIncomingWsDetail', {
              uiLang,
              cid,
              preview: t.slice(0, 120),
            });
            payloadForStore = {
              ...payloadForStore,
              text: t,
              message: t,
              mensaje: t,
              display_text: t,
            };
          } else {
            logEvent('TRANSLATION_MISSING', 'MessagesContext.processIncomingWsDetail', {
              uiLang,
              cid,
              mapKeys: Object.keys(map),
            });
          }
        } else {
          logEvent('TRANSLATION_MISSING', 'MessagesContext.processIncomingWsDetail', {
            uiLang,
            cid,
            reason: 'no_map',
          });
        }
      }
      if (isTraceMsg) {
        const currentUserId = typeof socketService?.userId !== 'undefined' ? Number(socketService.userId) : null;
        console.log('[AUTH_DEBUG][MessagesContext_PROCESS_OUTPUT]', JSON.stringify({
          message_id: payloadForStore?.id ?? payloadForStore?.mensaje_id,
          currentUserId,
          remitente_id: payloadForStore?.remitente_id,
          final_text: String(payloadForStore?.text ?? payloadForStore?.mensaje ?? '').slice(0, 200),
          final_display_text: String(payloadForStore?.display_text ?? '').slice(0, 200),
          final_mensaje: String(payloadForStore?.mensaje ?? '').slice(0, 200),
          final_traducciones_keys: payloadForStore?.traducciones ? Object.keys(payloadForStore.traducciones) : null,
          final_delivery_view: payloadForStore?.delivery_view,
          final_delivered_display_text: payloadForStore?.delivered_display_text?.slice(0, 200),
          skipAddMessage,
          cid,
        }));
        console.log('[DEBUG_TRACE][FE_PROCESS_OUTPUT]', JSON.stringify({
          final_text: String(payloadForStore?.text ?? payloadForStore?.mensaje ?? '').slice(0, 200),
          final_display_text: String(payloadForStore?.display_text ?? '').slice(0, 200),
          final_traducciones: payloadForStore?.traducciones,
          skipAddMessage,
          cid,
        }));
      }
    } catch {
      // noop
    }

    const fn = addMessageRef.current;
    if (skipAddMessage) {
      const mid = payloadForStore.id ?? payloadForStore.mensaje_id;
      const u = updateMessageRef.current;
      if (mid != null && u) {
        u(cid, mid, {
          text: payloadForStore.text,
          message: payloadForStore.text,
          mensaje: payloadForStore.text,
          display_text: payloadForStore.display_text,
          conversation_id: conversationId,
          conversacion_id: conversationId,
          conversationId,
        });
      }
    } else if (fn) {
      fn(cid, payloadForStore);
    }
    devLog('[MSG_UI_UPDATE][STORE]', { cid, message_id: data?.id ?? data?.mensaje_id });
    setTypingUsers((prev) => {
      const copy = { ...prev };
      delete copy[cid];
      return copy;
    });
    devLog('[ARCHAT][WS] message stored', cid);
  }, []);

  /**
   * Realtime vía `archat:new_message` (socketService; el bus `publishIncomingChatMessage` va en el mismo servicio).
   * Author: SIVOL® — FIX REALTIME DIRECTO — 2026-04-01
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onConversationOpened = (event) => {
      const rawCid =
        event?.detail?.conversationId ??
        event?.detail?.conversation_id ??
        event?.detail?.conversacion_id;
      if (rawCid == null || rawCid === '') return;
      const cid = String(rawCid).trim();
      if (cid === '' || cid === 'null' || cid === 'undefined') return;
      clearPendingIncomingForConversation(cid);
    };
    window.addEventListener('archat:conversation_opened', onConversationOpened);
    return () => window.removeEventListener('archat:conversation_opened', onConversationOpened);
  }, []);

  useEffect(() => {
    const onNewMessage = (event) => {
      const raw = event?.detail;
      if (!raw) return;
      const uidMerge = socketService.userId != null ? Number(socketService.userId) : null;
      if (uidMerge != null && Number.isFinite(uidMerge)) {
        mergePendingSnapshotOnlyFromPayload(raw, activeConversationIdRef.current, uidMerge);
      }
      const msg = normalizeIncomingMessage(raw);
      const cidRaw = msg.conversacion_id ?? msg.conversation_id ?? '';
      const cidStr = String(cidRaw).trim();
      if (cidStr === '' || cidStr === 'undefined' || cidStr === 'null') {
        if (typeof console !== 'undefined') {
          console.error('[CONV_ID_MISSING_CRITICAL]', msg);
        }
        return;
      }
      const cid = cidStr;

      const mid = msg.id ?? msg.message_id ?? msg.mensaje_id;
      if (mid == null || String(mid).trim() === '') return;

      const normalized = {
        ...msg,
        id: msg.id ?? msg.message_id ?? msg.mensaje_id,
        temp_id: msg.temp_id ?? null,
        __id: getMessageId(msg),
      };
      normalized.__id = getMessageId(normalized);

      addMessage(cid, normalized);
      if (hasAttachmentPayload(normalized) && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('archat:attachment_realtime_refresh', {
            detail: {
              conversation_id: cid,
              conversacion_id: cid,
              message_id: normalized.id ?? normalized.mensaje_id ?? null,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent('archat:storage_usage_changed', {
            detail: {
              reason: 'incoming_attachment_realtime',
              conversation_id: cid,
              conversacion_id: cid,
              ts: Date.now(),
            },
          })
        );
      }

      logEvent('MESSAGE_APPEND_DIRECT', 'MessagesContext', {
        id: normalized.id,
        cid,
      });

      setTimeout(() => {
        try {
          processIncomingWsDetail(raw, { skipAddMessage: true });
        } catch (err) {
          logEvent('POST_PROCESS_ERROR', 'MessagesContext', {
            error: err?.message ?? String(err),
          });
        }
      }, 0);
    };

    window.addEventListener('archat:new_message', onNewMessage);
    return () => window.removeEventListener('archat:new_message', onNewMessage);
  }, [addMessage, processIncomingWsDetail]);

  useEffect(() => {
    const start = (e) => {
      const { conversation_id, user_id, name } = e.detail || {};
      const cid = toConversationId(conversation_id);
      if (cid == null) return;
      const currentUserId = socketService.userId != null ? Number(socketService.userId) : null;
      const isOwnTyping = currentUserId != null && Number(user_id) === currentUserId;
      if (isOwnTyping) return;
      setTypingUsers((prev) => ({
        ...prev,
        [cid]: { name: name || 'Usuario', ts: Date.now() }
      }));
    };
    const stop = (e) => {
      const { conversation_id } = e.detail || {};
      const cid = toConversationId(conversation_id);
      if (cid == null) return;
      setTypingUsers((prev) => {
        const copy = { ...prev };
        delete copy[cid];
        return copy;
      });
    };
    window.addEventListener('archat:typing_start', start);
    window.addEventListener('archat:typing_stop', stop);
    return () => {
      window.removeEventListener('archat:typing_start', start);
      window.removeEventListener('archat:typing_stop', stop);
    };
  }, []);

  useEffect(() => {
    const onDelivered = (e) => {
      const { message_id, conversation_id } = e.detail || {};
      const numericCid = toConversationId(conversation_id);
      const mid = message_id != null ? Number(message_id) : null;
      if (numericCid == null || mid == null || Number.isNaN(mid)) return;
      setMessagesByConversation((prev) => {
        let next = { ...prev };
        let changedAny = false;
        for (const key of conversationBucketKeysForCid(prev, numericCid)) {
          const list = [...(prev[key] ?? [])];
          const mapped = list.map((m) => {
            const id = m?.id ?? m?.mensaje_id ?? m?.raw?.id ?? m?.raw?.mensaje_id;
            const idNum = Number(id);
            const idMatch =
              (Number.isFinite(idNum) && idNum === mid) || (id != null && String(id) === String(mid));
            if (idMatch) return { ...m, status: 'delivered', delivery_status: 'delivered' };
            return m;
          });
          if (mapped.some((m, i) => m !== list[i])) {
            next[key] = [...mapped];
            changedAny = true;
            logStateImmutabilityCheck(prev, next, key, next[key]);
          }
        }
        return changedAny ? next : prev;
      });
    };
    const onRead = (e) => {
      const { message_id, conversation_id } = e.detail || {};
      const numericCid = toConversationId(conversation_id);
      const mid = message_id != null ? Number(message_id) : null;
      if (numericCid == null || mid == null || Number.isNaN(mid)) return;
      setMessagesByConversation((prev) => {
        let next = { ...prev };
        let changedAny = false;
        for (const key of conversationBucketKeysForCid(prev, numericCid)) {
          const list = [...(prev[key] ?? [])];
          const mapped = list.map((m) => {
            const id = m?.id ?? m?.mensaje_id ?? m?.raw?.id ?? m?.raw?.mensaje_id;
            const idNum = Number(id);
            const idMatch =
              (Number.isFinite(idNum) && idNum === mid) || (id != null && String(id) === String(mid));
            if (idMatch) return { ...m, status: 'read', delivery_status: 'read' };
            return m;
          });
          if (mapped.some((m, i) => m !== list[i])) {
            next[key] = [...mapped];
            changedAny = true;
            logStateImmutabilityCheck(prev, next, key, next[key]);
          }
        }
        return changedAny ? next : prev;
      });
    };
    window.addEventListener('archat:message_delivered', onDelivered);
    window.addEventListener('archat:message_read', onRead);
    return () => {
      window.removeEventListener('archat:message_delivered', onDelivered);
      window.removeEventListener('archat:message_read', onRead);
    };
  }, []);

  useEffect(() => {
    const handleAvatarUpdated = (event) => {
      const payload = event?.detail || {};
      const targetUserId = Number(payload?.userId ?? payload?.id ?? payload?.usuario_id ?? 0);
      const avatarUrl = String(
        payload?.profile_avatar_url ??
          payload?.avatar_url ??
          payload?.avatar ??
          payload?.foto_perfil ??
          payload?.profile_avatar_full_url ??
          ''
      ).trim();
      if (!Number.isFinite(targetUserId) || targetUserId <= 0 || avatarUrl === '') return;

      setMessagesByConversation((prev) => {
        if (!prev || typeof prev !== 'object') return prev;
        let changedAny = false;
        const next = { ...prev };

        Object.keys(prev).forEach((key) => {
          const list = Array.isArray(prev[key]) ? prev[key] : [];
          if (list.length === 0) return;
          let bucketChanged = false;
          const mapped = list.map((msg) => {
            const senderId = Number(
              msg?.remitente_id ??
                msg?.sender_id ??
                msg?.emisor_id ??
                msg?.usuario_id ??
                msg?.raw?.remitente_id ??
                msg?.raw?.sender_id ??
                msg?.raw?.emisor_id ??
                msg?.raw?.usuario_id ??
                0
            );
            if (senderId !== targetUserId) return msg;
            bucketChanged = true;
            return {
              ...msg,
              profile_avatar_url: avatarUrl,
              avatar_url: avatarUrl,
              avatar: avatarUrl,
              foto_perfil: avatarUrl,
              profile_avatar_full_url: avatarUrl,
              avatar_version: payload?.avatar_version ?? msg?.avatar_version ?? null,
              raw: {
                ...(msg?.raw && typeof msg.raw === 'object' ? msg.raw : {}),
                profile_avatar_url: avatarUrl,
                avatar_url: avatarUrl,
                avatar: avatarUrl,
                foto_perfil: avatarUrl,
                profile_avatar_full_url: avatarUrl,
                avatar_version:
                  payload?.avatar_version ??
                  msg?.raw?.avatar_version ??
                  msg?.avatar_version ??
                  null,
              },
            };
          });
          if (bucketChanged) {
            next[key] = mapped;
            changedAny = true;
          }
        });

        return changedAny ? next : prev;
      });
    };

    window.addEventListener('archat:avatar-updated', handleAvatarUpdated);
    return () => window.removeEventListener('archat:avatar-updated', handleAvatarUpdated);
  }, []);

  /** Auto-limpieza: typing infinito desaparece si el otro cierra pestaña (ts > 4s) */
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers((prev) => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        const now = Date.now();
        const staleIds = [];
        for (const cid of Object.keys(prev)) {
          const entry = prev[cid];
          if (entry?.ts != null && now - entry.ts > 4000) {
            staleIds.push(cid);
          }
        }
        if (staleIds.length === 0) return prev;
        const next = { ...prev };
        staleIds.forEach((cid) => {
          delete next[cid];
        });
        return next;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const activeId = String(activeConversationId || '');

  const value = useMemo(
    () => ({
      messagesByConversation,
      typingUsers,
      activeConversationId,
      activeConversationIdNormalized: activeId,
      setActiveConversationId,
      favoriteIds,
      setFavoriteIds,
      addMessage,
      updateMessage,
      setConversationMessages,
      mergeConversation,
      getMessages,
      appendOptimisticSelfMessage,
    }),
    [
      messagesByConversation,
      typingUsers,
      activeConversationId,
      activeId,
      setActiveConversationId,
      favoriteIds,
      addMessage,
      updateMessage,
      setConversationMessages,
      mergeConversation,
      getMessages,
      appendOptimisticSelfMessage,
    ]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__archatMessagesByConversation = messagesByConversation;
    window.__ARCHAT_STORE = messagesByConversation;
    return () => {
      delete window.__archatMessagesByConversation;
      delete window.__ARCHAT_STORE;
    };
  }, [messagesByConversation]);

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessagesStore = () => {
  const ctx = useContext(MessagesContext);
  if (!ctx) throw new Error('useMessagesStore debe usarse dentro de MessagesProvider');
  return ctx;
};

export default MessagesContext;
